/**
 * Modal pour l'import multiple de sorties via Excel.
 * Ce fichier est NOUVEAU et n'affecte pas les composants existants.
 */
import React, { useState } from "react";
import {
  FaCheckCircle,
  FaDownload,
  FaExclamationTriangle,
  FaFileExcel,
  FaInfoCircle,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { useImportSortiesExcelMutation } from "../../actions/depenses/importMutation";
import ExcelDropzone from "../../components/FileUpload/ExcelDropzone";
import ImportResultsDisplay from "../../components/ImportResults/ImportResultsDisplay";
import { generateSortiesTemplate } from "../../utils/excelTemplateSorties";
import "./ModalImportEntrees.css";

// Configuration du modal
Modal.setAppElement("#root");

const ModalImportSorties = ({ isOpen, onClose, dossierId, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);

  // Utiliser la mutation Redux
  const [importSorties, { isLoading: loading }] =
    useImportSortiesExcelMutation();

  // Gérer la sélection du fichier
  const handleFileSelect = (selectedFile) => {
    setFile(selectedFile);
    setResults(null);
  };

  // Télécharger le template Excel
  const handleDownloadTemplate = () => {
    try {
      generateSortiesTemplate();
      toast.success("Template téléchargé avec succès");
    } catch (error) {
      console.error("Erreur téléchargement template:", error);
      toast.error("Erreur lors du téléchargement du template");
    }
  };

  const normalizeResponse = (data = {}) => ({
    success: data.success ?? false,
    message: data.message ?? "",
    sorties_creees: data.sorties_creees ?? 0,
    entrees_creees: data.sorties_creees ?? 0,
    produits_crees: data.produits_crees ?? 0,
    parsing_errors: data.errors ?? [],
    lignes_ignorees: data.lignes_ignorees ?? [],
    erreurs_creation: data.erreurs ?? [],
    erreurs: data.erreurs ?? [],
    aide: data.aide ?? null,
  });

  // Importer le fichier
  const handleImport = async () => {
    if (!file) {
      setResults({
        success: false,
        message: "Veuillez sélectionner un fichier Excel.",
        sorties_creees: 0,
        produits_crees: 0,
        parsing_errors: [],
        erreurs_creation: [],
      });
      return;
    }

    if (!dossierId) {
      setResults({
        success: false,
        message: "Identifiant du dossier manquant.",
        sorties_creees: 0,
        produits_crees: 0,
        parsing_errors: [],
        erreurs_creation: [],
      });
      return;
    }

    setResults(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("dossier_id", dossierId);

      const response = await importSorties(formData).unwrap();
      const normalized = normalizeResponse(response);
      setResults(normalized);

      if (normalized.success) {
        setTimeout(() => {
          if (onSuccess) onSuccess();
        }, 1500);
      }
    } catch (error) {
      const apiData = error?.data || error?.error?.data || {};
      const normalized = normalizeResponse(apiData);
      setResults(normalized);
    }
  };

  const handleClose = () => {
    setFile(null);
    setResults(null);
    setShowInstructions(false);
    onClose();
  };

  const handleCloseWithRefresh = () => {
    if (results && results.sorties_creees > 0) {
      if (onSuccess) onSuccess();
    }
    handleClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      className="modal-import-entrees"
      overlayClassName="modal-import-overlay"
      contentLabel="Importer des sorties"
    >
      <div className="modal-import-container">
        {/* En-tête moderne */}
        <div className="modal-import-header">
          <div className="header-content">
            <div className="header-icon">
              <FaFileExcel />
            </div>
            <div className="header-text">
              <h2>Importer des sorties</h2>
              <p>Importez plusieurs sorties en une seule fois via Excel</p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close-modal"
            onClick={handleClose}
            disabled={loading}
          >
            <FaTimes />
          </button>
        </div>

        {/* Contenu */}
        <div className="modal-import-content">
          {/* Section 1 : Télécharger le template */}
          <div className="import-section">
            <div className="section-header">
              <div className="section-number">1</div>
              <h3>Télécharger le modèle Excel</h3>
            </div>
            <p className="section-description">
              Téléchargez le fichier modèle, remplissez-le avec vos données,
              puis importez-le.
            </p>
            <div className="section-actions">
              <button
                type="button"
                className="btn-download-template"
                onClick={handleDownloadTemplate}
                disabled={loading}
              >
                <FaDownload />
                <span>Télécharger le modèle</span>
              </button>
              <button
                type="button"
                className="btn-show-instructions"
                onClick={() => setShowInstructions(!showInstructions)}
              >
                <FaInfoCircle />
                <span>
                  {showInstructions ? "Masquer" : "Voir"} les instructions
                </span>
              </button>
            </div>

            {/* Instructions dépliables */}
            {showInstructions && (
              <div className="instructions-panel">
                <h4><FaInfoCircle /> Instructions d&apos;import</h4>
                <div className="instructions-content">
                  {[
                    { titre: "Champs obligatoires", texte: "Date, Intitulé, Fournisseur, Nom produit, Description, Quantité (> 0), Prix unitaire (> 0)." },
                    { titre: "Compte comptable", texte: "Optionnel (ex: 40110025). S'il n'est pas renseigné, le compte du fournisseur est incrémenté automatiquement." },
                    { titre: "Taxes", texte: "Valeurs acceptées uniquement : 0 %, 10 % ou 18 %." },
                    { titre: "Sources de paiement", texte: "Valeurs acceptées : banque, caisse, wave, orange_money." },
                    { titre: "Montant payé", texte: "Ne peut pas dépasser Quantité × Prix unitaire par ligne, ni le total TTC de la sortie." },
                    { titre: "Format des dates", texte: "Format JJ/MM/AAAA (exemple : 15/01/2026)." },
                    { titre: "Groupement", texte: "Les lignes avec la même Date + Intitulé + Fournisseur sont fusionnées en une seule sortie. Même produit et même prix : quantités fusionnées. Même produit et prix différents : import bloqué." },
                    { titre: "Règle tout ou rien", texte: "Si une seule ligne est invalide, aucune sortie n'est créée. Toutes les erreurs s'affichent dans ce modal." },
                    { titre: "Fournisseurs nouveaux", texte: "Les fournisseurs absents de la base sont créés automatiquement." },
                  ].map(({ titre, texte }) => (
                    <div className="instruction-item" key={titre}>
                      <FaCheckCircle className="check-icon" />
                      <div><strong>{titre} :</strong><p>{texte}</p></div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Section 2 : Upload du fichier */}
          {!results && (
            <div className="import-section">
              <div className="section-header">
                <div className="section-number">2</div>
                <h3>Sélectionner votre fichier</h3>
              </div>
              <p className="section-description">
                Glissez-déposez votre fichier Excel ou cliquez pour le
                sélectionner.
              </p>
              <ExcelDropzone onFileSelect={handleFileSelect} />
            </div>
          )}

          {/* Section 3 : Résultats */}
          {results && (
            <div className="import-section">
              <div className="section-header">
                <div className="section-number">
                  {results.success ? (
                    <FaCheckCircle style={{ color: "#22c55e" }} />
                  ) : (
                    <FaExclamationTriangle style={{ color: "#ef4444" }} />
                  )}
                </div>
                <h3>Résultats de l&apos;import</h3>
              </div>
              <ImportResultsDisplay
                results={results}
                onClose={handleCloseWithRefresh}
                entityLabel="sortie"
              />
            </div>
          )}
        </div>

        {/* Pied de page */}
        {!results && (
          <div className="modal-import-footer">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Annuler
            </button>
            {file && (
              <button
                type="button"
                className="btn-import"
                onClick={handleImport}
                disabled={loading || !file}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    <span>Import en cours...</span>
                  </>
                ) : (
                  <>
                    <FaUpload />
                    <span>Importer</span>
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ModalImportSorties;
